import { useMemo } from "react";
import { mkConfig, generateCsv, download } from "export-to-csv";
import * as XLSX from "xlsx";
import DivContainer from "../../components/layouts/DivContainer";
import FullScreenSpinner from "../../components/FullScreenSpinner";
import Modal from "../../components/Modals/Modal";
import { BtnGeneral } from "../../styles/Buttons";
import useAuth from "../../hooks/useAuth";
import { useCarguePacientesMasivos } from "../../hooks/carguePacientesMasivos/useCarguePacientesMasivos";
import SearchableSelect from "../../components/Common/SearchableSelect";
import {
  ActionBar,
  BatchSizeField,
  DateTimeInput,
  EmptyState,
  ExecutionModeButton,
  ExecutionModeGroup,
  HeaderCard,
  HelperText,
  HiddenInput,
  InfoCard,
  InfoGrid,
  InfoLabel,
  InfoValue,
  ModalActions,
  ModalText,
  Page,
  ProgressCard,
  ProgressCluster,
  ProgressBarSection,
  ProgressFill,
  ProgressHeader,
  ProgressPercent,
  ProgressTrack,
  SectionTitle,
  StatusActions,
  StatusBanner,
  StatusMeta,
  StatusText,
  StatusTitle,
  SummaryCard,
  SummaryGrid,
  SummaryValue,
  SelectionAlertContent,
  Table,
  TableActions,
  TableCard,
  TableHeaderBar,
  TableHeaderContent,
  TableWrap,
  Title,
  WarningBody,
  WarningItem,
  WarningList,
  WarningTitle,
} from "./CarguePacientesMasivos.styles";

const MAX_VISIBLE_ISSUES = 300;

const formatPercent = (value) => {
  const numericValue = Math.max(0, Number(value) || 0);
  const rounded = Math.round(numericValue * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}%` : `${rounded.toFixed(1)}%`;
};

const formatDuration = (value) => {
  const ms = Math.max(0, Number(value) || 0);

  if (ms >= 60000) {
    return `${(ms / 60000).toFixed(1)} min`;
  }

  if (ms >= 1000) {
    return `${(ms / 1000).toFixed(1)} s`;
  }

  return `${ms} ms`;
};

const formatDateTime = (value) => {
  if (!value) {
    return "Sin fecha";
  }

  const normalizedValue = String(value).includes("T") ? value : String(value).replace(" ", "T");
  const date = new Date(normalizedValue);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("es-CO", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
};

const getLastJobTone = (status) => {
  const normalizedStatus = String(status || "").toLowerCase();
  if (normalizedStatus === "completed") {
    return "success";
  }
  if (normalizedStatus === "failed" || normalizedStatus === "cancelled") {
    return "danger";
  }
  return "info";
};

const getLastJobLabel = (status) => {
  const normalizedStatus = String(status || "").toLowerCase();
  if (normalizedStatus === "scheduled") {
    return "Programado";
  }
  if (normalizedStatus === "running") {
    return "En ejecución";
  }
  if (normalizedStatus === "completed") {
    return "Completado";
  }
  if (normalizedStatus === "failed") {
    return "Fallido";
  }
  if (normalizedStatus === "cancelled") {
    return "Cancelado";
  }
  return "Sin estado";
};

const getExecutionModeLabel = (value) => {
  const normalizedValue = String(value || "").toLowerCase();
  if (normalizedValue === "immediate") {
    return "Procesar ahora";
  }
  if (normalizedValue === "scheduled") {
    return "Programado";
  }
  return "Sin definir";
};

const createExportRowsFromIssues = (issues) =>
  (issues || []).map((item) => ({
    Tipo: item.status === "warning" ? "Advertencia" : "Error",
    Fila: item.row ?? "",
    Identificación: item.identificacion || "-",
    Detalle: item.message || "",
  }));

export default function CarguePacientesMasivos() {
  const { sedes } = useAuth();
  const { state, actions } = useCarguePacientesMasivos(sedes);
  const hasPendingJobs = state.pendingJobs.length > 0;
  const lastJobStatus = String(state.lastJob?.status || "").toLowerCase();
  const lastJobErrorsCount = Number(state.lastJob?.errors_count || 0);
  const lastJobWarningsCount = Number(state.lastJob?.warnings_count || 0);
  const lastJobInactivatedCount = Number(state.lastJob?.inactivated_count || 0);
  const lastJobHasFatalError = String(state.lastJob?.fatal_error_message || "").trim() !== "";
  const lastJobHasErrors =
    lastJobStatus === "failed" ||
    lastJobStatus === "cancelled" ||
    lastJobErrorsCount > 0 ||
    lastJobHasFatalError;
  const shouldShowLastJobDownloadButton = lastJobErrorsCount > 0 || lastJobWarningsCount > 0;
  const lastJobCompletedSuccessfully = lastJobStatus === "completed" && !lastJobHasErrors;

  const invalidRows = useMemo(
    () => state.validation.filter((item) => !item.valid),
    [state.validation]
  );
  const visibleInvalidRows = useMemo(
    () => invalidRows.slice(0, MAX_VISIBLE_ISSUES),
    [invalidRows]
  );
  const hasHiddenIssues =
    invalidRows.length > visibleInvalidRows.length ||
    state.issueTotals.errors > state.errors.length ||
    state.issueTotals.warnings > state.warnings.length;
  const showLoader = state.loadingFile || state.processing;
  const hasBatchProgress = state.progress.totalBatches > 0;
  const progressPercentage = state.progress.percentage || 0;
  const selectedMunicipioLabel = useMemo(
    () =>
      state.availableMunicipios.find((municipio) => municipio.codigo === state.selectedMunicipio)
        ?.label || "No seleccionado",
    [state.availableMunicipios, state.selectedMunicipio]
  );
  const loaderText = state.loadingFile
    ? "Leyendo archivo, por favor espere..."
    : state.processing && state.executionMode === "schedule" && !hasBatchProgress
      ? "Programando cargue, por favor espere..."
      : hasBatchProgress
      ? `Procesando lote ${state.progress.currentBatch} de ${state.progress.totalBatches}, por favor espere...`
      : "Procesando, por favor espere...";
  const lastJobTone = getLastJobTone(state.lastJob?.status);
  const lastJobLabel = getLastJobLabel(state.lastJob?.status);
  const shouldShowIncompleteNotice = !!state.lastJob && lastJobHasErrors;
  const shouldShowLastExecutedBanner = !hasPendingJobs && !!state.lastJob;
  const selectionAlertRows = useMemo(
    () =>
      state.selectionAlert.warnings.map((warning) => {
        const municipioLabel = warning.municipio
          ? warning.municipioLabel || warning.municipio
          : "Sin municipio en Excel";

        let detail = "";
        if (warning.type === "codmuni_diferente_al_seleccionado") {
          detail = `El campo Codmuni del Excel debe ser ${warning.selectedLabel} para todas las filas.`;
        }
        if (warning.type === "codmuni_vacio_en_excel") {
          detail = `El campo Codmuni es obligatorio y debe coincidir con ${warning.selectedLabel}.`;
        }
        if (warning.type === "mpio_residencia_diferente_al_seleccionado") {
          detail = `El campo Mpio_residencia de estos registros es distinto a ${warning.selectedLabel}.`;
        }
        if (warning.type === "mpio_residencia_vacio_en_excel") {
          detail = `El campo Mpio_residencia es obligatorio para estos registros.`;
        }

        return {
          key: `${warning.type}-${warning.municipio || "empty"}-${warning.patientCount}`,
          municipioLabel,
          patientCount: warning.patientCount,
          percentage: warning.percentage || 0,
          detail,
        };
      }),
    [state.selectionAlert.warnings]
  );
  const selectionAlertSummary = state.selectionAlert.summary || {
    selectedLabel: "",
    selectedName: "",
    mismatchCount: 0,
    mismatchPercentage: 0,
    codmuniMismatchCount: 0,
    codmuniMismatchPercentage: 0,
    residenciaMismatchCount: 0,
    residenciaMismatchPercentage: 0,
    totalRows: 0,
    isBlocked: false,
    hasCodmuniMismatch: false,
    hasResidenciaThresholdExceeded: false,
  };
  const logSummaryText = useMemo(() => {
    const validationCount = invalidRows.length;
    const errorCount = state.issueTotals.errors;
    const warningCount = state.issueTotals.warnings;
    const parts = [];

    if (validationCount > 0) {
      parts.push(`${validationCount} validación${validationCount === 1 ? "" : "es"}`);
    }

    if (errorCount > 0) {
      parts.push(`${errorCount} error${errorCount === 1 ? "" : "es"}`);
    }

    if (warningCount > 0) {
      parts.push(`${warningCount} advertencia${warningCount === 1 ? "" : "s"}`);
    }

    if (parts.length === 0) {
      return "No hay novedades registradas para exportar.";
    }

    return `Se registraron ${parts.join(", ")}.`;
  }, [invalidRows.length, state.issueTotals.errors, state.issueTotals.warnings]);
  const logRows = useMemo(
    () => [
      ...visibleInvalidRows.map((item) => ({
        key: `invalid-${item.row}`,
        tipo: "Validación",
        tone: "validation",
        fila: item.row,
        identificacion: "-",
        detalle: `Faltan campos: ${item.missing.join(", ")}`,
      })),
      ...state.errors.map((item, index) => ({
        key: `error-${index}`,
        tipo: "Error",
        tone: "error",
        fila: item.row,
        identificacion: item.identificacion || "-",
        detalle: item.message,
      })),
      ...state.warnings.map((item, index) => ({
        key: `warning-${index}`,
        tipo: "Advertencia",
        tone: "warning",
        fila: item.row,
        identificacion: item.identificacion || "-",
        detalle: item.message,
      })),
    ],
    [state.errors, state.warnings, visibleInvalidRows]
  );

  const exportableLogRows = useMemo(
    () =>
      logRows.map((item) => ({
        Tipo: item.tipo,
        Fila: item.fila ?? "",
        Identificación: item.identificacion,
        Detalle: item.detalle,
      })),
    [logRows]
  );

  const handleExportCsv = () => {
    if (exportableLogRows.length === 0) {
      return;
    }

    const config = mkConfig({
      useKeysAsHeaders: true,
      filename: "log_detalle_cargue_pacientes",
    });
    download(config)(generateCsv(config)(exportableLogRows));
  };

  const handleExportExcel = () => {
    if (exportableLogRows.length === 0) {
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(exportableLogRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Log cargue");
    XLSX.writeFile(workbook, "log_detalle_cargue_pacientes.xlsx");
  };

  const handleExportLastJobErrorsExcel = async () => {
    if (!state.lastJob?.id) {
      return;
    }

    const issues =
      state.jobIssuesById[state.lastJob.id] || (await actions.loadJobIssues(state.lastJob.id));
    if (issues.length === 0) {
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(createExportRowsFromIssues(issues));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Detalle ultimo proceso");
    XLSX.writeFile(workbook, "detalle_ultimo_cargue_pacientes.xlsx");
  };

  const handleOpenHistoryModal = async () => {
    actions.setHistoryModalOpen(true);
    await actions.loadHistoryJobs();
  };

  const handleCloseHistoryModal = () => {
    actions.setHistoryModalOpen(false);
  };

  const handleExportHistoryJobExcel = async (job) => {
    const jobId = Number(job?.id || 0);
    if (jobId <= 0) {
      return;
    }

    const issues = state.jobIssuesById[jobId] || (await actions.loadJobIssues(jobId));
    if (issues.length === 0) {
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(createExportRowsFromIssues(issues));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Detalle cargue");
    XLSX.writeFile(workbook, `detalle_cargue_${jobId}.xlsx`);
  };

  return (
    <DivContainer>
      {showLoader && <FullScreenSpinner text={loaderText} />}
      <Page>
        <HeaderCard>
          <Title>Cargue Masivo de Pacientes</Title>
          {(state.loadingPendingJobs || state.loadingLastJob) && (
            <HelperText>Consultando el estado de los cargues del usuario...</HelperText>
          )}
          {shouldShowLastExecutedBanner && (
            <StatusBanner tone={lastJobTone}>
              <StatusTitle tone={lastJobTone}>Ultimo proceso ejecutado: {lastJobLabel}</StatusTitle>
              <StatusText tone={lastJobTone}>
                Municipio: {state.lastJob?.municipio_label || state.lastJob?.id_muni || "Sin municipio"}.
                Procesadas {state.lastJob.processed_rows || 0} de {state.lastJob.total_rows || 0} filas.
              </StatusText>
              <StatusText tone={lastJobTone}>
                Tipo: {getExecutionModeLabel(state.lastJob?.execution_mode)}. Inicio{" "}
                {formatDateTime(state.lastJob?.started_at || state.lastJob?.created_at)}.
              </StatusText>
              {lastJobHasErrors && (
                <StatusMeta tone={lastJobTone}>
                  Errores: {lastJobErrorsCount} | Advertencias: {lastJobWarningsCount} |
                  Inactivados: {lastJobInactivatedCount}
                </StatusMeta>
              )}
              {lastJobCompletedSuccessfully && (
                <StatusText tone={lastJobTone}>
                  El ultimo cargue se completo correctamente.
                </StatusText>
              )}
              {shouldShowIncompleteNotice && (
                <StatusText tone={lastJobTone}>
                  El ultimo cargue no se completo correctamente y debe volver a cargar el archivo y validar la informacion.
                </StatusText>
              )}
              {state.lastJob.fatal_error_message && (
                <StatusText tone={lastJobTone}>
                  Motivo reportado: {state.lastJob.fatal_error_message}
                </StatusText>
              )}
              {shouldShowLastJobDownloadButton && (
                <StatusActions>
                  <BtnGeneral
                    bg="excel"
                    padding="10px 16px"
                    dp="inline-flex"
                    disabled={state.loadingJobIssuesId === Number(state.lastJob?.id || 0) || !state.lastJob}
                    onClick={handleExportLastJobErrorsExcel}
                  >
                    {state.loadingJobIssuesId === Number(state.lastJob?.id || 0)
                      ? "Consultando detalle..."
                      : "Descargar detalle ultimo proceso"}
                  </BtnGeneral>
                </StatusActions>
              )}
            </StatusBanner>
          )}
        </HeaderCard>

        <InfoGrid>
          <InfoCard>
            <InfoLabel>Archivo</InfoLabel>
            <InfoValue>{state.fileName || "Sin archivo seleccionado"}</InfoValue>
          </InfoCard>
          <InfoCard>
            <InfoLabel>Municipio seleccionado</InfoLabel>
            <InfoValue>{selectedMunicipioLabel}</InfoValue>
          </InfoCard>
          <InfoCard>
            <InfoLabel>Filas cargadas</InfoLabel>
            <InfoValue>{state.rows.length}</InfoValue>
          </InfoCard>
        </InfoGrid>

        <ActionBar>
          <ExecutionModeGroup>
            <ExecutionModeButton
              type="button"
              active={state.executionMode === "process"}
              onClick={() => actions.setExecutionMode("process")}
              disabled={state.processing || state.loadingFile}
            >
              Procesar ahora
            </ExecutionModeButton>
            <ExecutionModeButton
              type="button"
              active={state.executionMode === "schedule"}
              onClick={() => actions.setExecutionMode("schedule")}
              disabled={state.processing || state.loadingFile}
            >
              Programar Cargue
            </ExecutionModeButton>
          </ExecutionModeGroup>
          <BatchSizeField>
            Municipio
            <SearchableSelect
              value={state.selectedMunicipio}
              disabled={
                state.processing || state.loadingFile || state.availableMunicipios.length === 0
              }
              onValueChange={(val) => actions.setSelectedMunicipio(val)}
              options={state.availableMunicipios}
              getOptionValue={(m) => m.codigo}
              getOptionLabel={(m) => m.label}
              placeholder="Seleccione municipio..."
              multiple={false}
              searchable={true}
              sx={{
                minWidth: "220px",
                maxWidth: "320px",
                "& .MuiOutlinedInput-root": {
                  height: "36px",
                  minHeight: "36px",
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontFamily: "var(--font-body)",
                  bgcolor: "#ffffff",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#cbd5e1",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#2563eb",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#2563eb",
                },
              }}
              selectSx={{
                height: "36px",
                minHeight: "36px",
                "& .MuiSelect-select": {
                  padding: "6px 10px !important",
                  fontSize: "13px",
                  fontFamily: "var(--font-body)",
                  color: "#111827",
                },
              }}
            />
          </BatchSizeField>
          {state.executionMode === "schedule" && (
            <BatchSizeField>
              Fecha y hora
              <DateTimeInput
                type="datetime-local"
                value={state.scheduledAt}
                disabled={state.processing || state.loadingFile}
                onChange={(event) => actions.setScheduledAt(event.target.value)}
              />
            </BatchSizeField>
          )}
          <HiddenInput
            id="cargue-pacientes-excel"
            type="file"
            accept=".xlsx,.xls"
            disabled={state.processing || state.loadingFile}
            onChange={(event) => actions.handleFileSelected(event.target.files?.[0])}
          />
          <BtnGeneral
            bg="excel"
            padding="10px 16px"
            dp="inline-flex"
            disabled={state.processing || state.loadingFile}
            onClick={() => document.getElementById("cargue-pacientes-excel")?.click()}
          >
            Seleccionar Archivo a Cargar
          </BtnGeneral>
          <BtnGeneral
            bg="green"
            padding="10px 16px"
            dp="inline-flex"
            disabled={
              state.processing ||
              state.rows.length === 0 ||
              state.selectedMunicipio === "" ||
              (state.executionMode === "schedule" && String(state.scheduledAt || "").trim() === "")
            }
            onClick={actions.submitUpload}
          >
            {state.processing
              ? state.executionMode === "schedule"
                ? "Programando..."
                : "Procesando..."
              : state.executionMode === "schedule"
                ? "Programar"
                : "Procesar"}
          </BtnGeneral>
          <BtnGeneral
            bg="gray"
            padding="10px 16px"
            dp="inline-flex"
            disabled={state.processing || state.loadingFile}
            onClick={() => {
              const input = document.getElementById("cargue-pacientes-excel");
              if (input) {
                input.value = "";
              }
              actions.resetState();
            }}
          >
            Limpiar
          </BtnGeneral>
          <BtnGeneral
            bg="blue"
            padding="10px 16px"
            dp="inline-flex"
            disabled={state.loadingHistoryJobs}
            onClick={handleOpenHistoryModal}
          >
            {state.loadingHistoryJobs ? "Consultando historico..." : "Ver historico"}
          </BtnGeneral>
          {hasBatchProgress && (
            <ProgressCluster>
              <ProgressCard>
                <InfoLabel>Lote actual</InfoLabel>
                <InfoValue>
                  {state.progress.currentBatch} / {state.progress.totalBatches}
                </InfoValue>
              </ProgressCard>
              <ProgressCard>
                <InfoLabel>Filas procesadas</InfoLabel>
                <InfoValue>
                  {state.progress.processedRows} / {state.rows.length}
                </InfoValue>
              </ProgressCard>
              <ProgressCard>
                <InfoLabel>Tiempo último lote</InfoLabel>
                <InfoValue>{formatDuration(state.timing.lastBatchMs)}</InfoValue>
              </ProgressCard>
              <ProgressCard>
                <InfoLabel>Tiempo backend lote</InfoLabel>
                <InfoValue>{formatDuration(state.timing.backendLastBatchMs)}</InfoValue>
              </ProgressCard>
            </ProgressCluster>
          )}
        </ActionBar>

        {state.availableMunicipios.length === 0 && (
          <HelperText>
            No tiene municipios disponibles a partir de las sedes habilitadas en su usuario.
          </HelperText>
        )}
        {state.excelMunicipios.length > 0 && (
          <HelperText>
            Municipios detectados en el Excel:{" "}
            {state.excelMunicipios.map((item) => `${item.codigo} (${item.count})`).join(", ")}
          </HelperText>
        )}

        {hasPendingJobs && (
          <TableCard>
            <TableHeaderBar>
              <TableHeaderContent>
                <SectionTitle>Procesos programados y en ejecucion</SectionTitle>
                <HelperText>
                  Se muestran todos los procesos del usuario que siguen pendientes de finalizar.
                </HelperText>
              </TableHeaderContent>
            </TableHeaderBar>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Estado</th>
                    <th>Municipio</th>
                    <th>Fecha programada</th>
                    <th>Total</th>
                    <th>Procesadas</th>
                    <th>Errores</th>
                    <th>Advertencias</th>
                  </tr>
                </thead>
                <tbody>
                  {state.pendingJobs.map((job) => (
                    <tr key={job.id}>
                      <td>{getExecutionModeLabel(job.execution_mode)}</td>
                      <td>{getLastJobLabel(job.status)}</td>
                      <td>{job.municipio_label || job.id_muni || "Sin municipio"}</td>
                      <td>{formatDateTime(job.scheduled_at)}</td>
                      <td>{job.total_rows || 0}</td>
                      <td>{job.processed_rows || 0}</td>
                      <td>{job.errors_count || 0}</td>
                      <td>{job.warnings_count || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          </TableCard>
        )}

        {hasBatchProgress && (
          <ProgressBarSection>
            <ProgressHeader>
              <div>
                <SectionTitle>Progreso del cargue</SectionTitle>
                <HelperText>
                  Lotes fijos de {state.progress.batchSize} filas. Tiempo total{" "}
                  {formatDuration(state.timing.totalElapsedMs)}. Promedio por lote{" "}
                  {formatDuration(state.timing.averageBatchMs)}.
                </HelperText>
              </div>
              <ProgressPercent>{progressPercentage}%</ProgressPercent>
            </ProgressHeader>
            <ProgressTrack>
              <ProgressFill value={progressPercentage} />
            </ProgressTrack>
            <HelperText>
              Procesadas {state.progress.processedRows} de {state.rows.length} filas. Backend
              acumulado {formatDuration(state.timing.backendTotalMs)}.
            </HelperText>
          </ProgressBarSection>
        )}

        {state.summary && (
          <SummaryGrid>
            <SummaryCard>
              <InfoLabel>Total</InfoLabel>
              <SummaryValue>{state.summary.total}</SummaryValue>
            </SummaryCard>
            <SummaryCard>
              <InfoLabel>Procesados</InfoLabel>
              <SummaryValue>{state.summary.procesados}</SummaryValue>
            </SummaryCard>
            <SummaryCard>
              <InfoLabel>Reasignados sede</InfoLabel>
              <SummaryValue>{state.summary.reasignados_sede}</SummaryValue>
            </SummaryCard>
            <SummaryCard>
              <InfoLabel>Rechazados</InfoLabel>
              <SummaryValue>{state.summary.rechazados}</SummaryValue>
            </SummaryCard>
            <SummaryCard>
              <InfoLabel>Sede principal</InfoLabel>
              <SummaryValue>{state.summary.asignados_sede_principal || 0}</SummaryValue>
            </SummaryCard>
            <SummaryCard>
              <InfoLabel>Conservan sede</InfoLabel>
              <SummaryValue>{state.summary.conservados_sede_actual || 0}</SummaryValue>
            </SummaryCard>
            <SummaryCard>
              <InfoLabel>Inactivados</InfoLabel>
              <SummaryValue>{state.summary.inactivados || 0}</SummaryValue>
            </SummaryCard>
          </SummaryGrid>
        )}

        {logRows.length > 0 && (
          <TableCard>
            <TableHeaderBar>
              <TableHeaderContent>
                <SectionTitle>Log de detalle del cargue</SectionTitle>
                <HelperText>
                  {logSummaryText} Registros visibles del proceso actual: {logRows.length}.
                  Exporte este detalle en Excel o CSV para revisión.
                </HelperText>
                {hasHiddenIssues && (
                  <HelperText>
                    Mostrando hasta {MAX_VISIBLE_ISSUES} novedades visibles por rendimiento.
                    Totales acumulados: validaciones {invalidRows.length}, errores{" "}
                    {state.issueTotals.errors}, advertencias {state.issueTotals.warnings}.
                  </HelperText>
                )}
              </TableHeaderContent>
              <TableActions>
                <BtnGeneral
                  bg="excel"
                  padding="10px 16px"
                  dp="inline-flex"
                  disabled={exportableLogRows.length === 0}
                  onClick={handleExportExcel}
                >
                  Exportar Excel
                </BtnGeneral>
                <BtnGeneral
                  bg="blue"
                  padding="10px 16px"
                  dp="inline-flex"
                  disabled={exportableLogRows.length === 0}
                  onClick={handleExportCsv}
                >
                  Exportar CSV
                </BtnGeneral>
              </TableActions>
            </TableHeaderBar>
            <TableWrap>
              <Table wideLastColumn>
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Fila</th>
                    <th>Identificacion</th>
                    <th>Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {logRows.map((row) => (
                    <tr key={row.key}>
                      <td>{row.tipo}</td>
                      <td>{row.fila ?? "-"}</td>
                      <td>{row.identificacion}</td>
                      <td>{row.detalle}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          </TableCard>
        )}
      </Page>

      <Modal
        isOpen={state.historyModalOpen}
        onClose={handleCloseHistoryModal}
        title="Historico de cargues"
        width="1100px"
        minH="280px"
        maxH="85vh"
      >
        <HelperText>Solo se listan los ultimos 15 procesos del usuario.</HelperText>
        {state.loadingHistoryJobs ? (
          <HelperText>Consultando historico...</HelperText>
        ) : state.historyJobs.length === 0 ? (
          <EmptyState>No hay cargues historicos para mostrar.</EmptyState>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Tipo</th>
                  <th>Estado</th>
                  <th>Municipio</th>
                  <th>Total</th>
                  <th>Procesadas</th>
                  <th>Errores</th>
                  <th>Advertencias</th>
                  <th>Inactivados</th>
                  <th>Accion</th>
                </tr>
              </thead>
              <tbody>
                {state.historyJobs.map((job) => {
                  const hasIssues =
                    Number(job?.errors_count || 0) > 0 || Number(job?.warnings_count || 0) > 0;
                  const isLoadingThisJob = state.loadingJobIssuesId === Number(job?.id || 0);

                  return (
                    <tr key={job.id}>
                      <td>{formatDateTime(job.finished_at || job.started_at || job.created_at)}</td>
                      <td>{getExecutionModeLabel(job.execution_mode)}</td>
                      <td>{getLastJobLabel(job.status)}</td>
                      <td>{job.municipio_label || job.id_muni || "Sin municipio"}</td>
                      <td>{job.total_rows || 0}</td>
                      <td>{job.processed_rows || 0}</td>
                      <td>{job.errors_count || 0}</td>
                      <td>{job.warnings_count || 0}</td>
                      <td>{job.inactivated_count || 0}</td>
                      <td>
                        {hasIssues ? (
                          <BtnGeneral
                            bg="excel"
                            padding="8px 12px"
                            dp="inline-flex"
                            disabled={isLoadingThisJob}
                            onClick={() => handleExportHistoryJobExcel(job)}
                          >
                            {isLoadingThisJob ? "Consultando..." : "Descargar detalle"}
                          </BtnGeneral>
                        ) : (
                          "Sin detalle"
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableWrap>
        )}
        <ModalActions>
          <BtnGeneral bg="gray" padding="10px 16px" dp="inline-flex" onClick={handleCloseHistoryModal}>
            Cerrar
          </BtnGeneral>
        </ModalActions>
      </Modal>

      <Modal
        isOpen={state.selectionAlert.isOpen}
        onClose={actions.cancelSelectionAlert}
        title="Validacion De Municipio Antes Del Cargue"
        width="760px"
        minH="520px"
        maxH="88vh"
        bodyOverflow="auto"
      >
        <SelectionAlertContent>
          <WarningItem tone={selectionAlertSummary.isBlocked ? "danger" : "warning"}>
            <WarningTitle tone={selectionAlertSummary.isBlocked ? "danger" : "warning"}>
              Validacion del municipio seleccionado
            </WarningTitle>
            <WarningBody tone={selectionAlertSummary.isBlocked ? "danger" : "warning"}>
              El municipio seleccionado es {selectionAlertSummary.selectedName || "No seleccionado"}.
              El campo Codmuni del Excel debe coincidir con ese municipio en todas las filas.
            </WarningBody>
            <WarningBody tone={selectionAlertSummary.isBlocked ? "danger" : "warning"}>
              Registros con Codmuni diferente o vacio:{" "}
              {formatPercent(selectionAlertSummary.codmuniMismatchPercentage)}.
            </WarningBody>
            <WarningBody tone={selectionAlertSummary.isBlocked ? "danger" : "warning"}>
              El porcentaje {formatPercent(selectionAlertSummary.residenciaMismatchPercentage)} corresponde
              a los pacientes cuyo Mpio_residencia es diferente al municipio seleccionado o viene vacio.
            </WarningBody>
            <WarningBody tone={selectionAlertSummary.isBlocked ? "danger" : "warning"}>
              {selectionAlertSummary.hasCodmuniMismatch
                ? "El Codmuni del Excel debe coincidir con el municipio seleccionado. Revise la base y vuelva a intentarlo."
                : selectionAlertSummary.hasResidenciaThresholdExceeded
                  ? "Con mas del 5% de Mpio_residencia diferentes al municipio seleccionado no se puede cargar la base de datos. Revise la base y vuelva a intentarlo."
                  : "Si este porcentaje supera el 5%, el sistema no permitira continuar."}
            </WarningBody>
          </WarningItem>
          <ModalText>
            Revise el detalle antes de decidir si desea continuar con el cargue.
          </ModalText>
          <WarningList>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <th>Municipio</th>
                    <th>Pacientes</th>
                    <th>%</th>
                    <th>Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {selectionAlertRows.map((row) => (
                    <tr key={row.key}>
                      <td>{row.municipioLabel}</td>
                      <td>{row.patientCount}</td>
                      <td>{formatPercent(row.percentage)}</td>
                      <td>{row.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          </WarningList>
          <ModalActions>
            <BtnGeneral bg="gray" padding="10px 16px" dp="inline-flex" onClick={actions.cancelSelectionAlert}>
              Cancelar
            </BtnGeneral>
            <BtnGeneral
              bg="green"
              padding="10px 16px"
              dp="inline-flex"
              disabled={selectionAlertSummary.isBlocked}
              onClick={actions.confirmSelectionAlert}
            >
              Continuar
            </BtnGeneral>
          </ModalActions>
        </SelectionAlertContent>
      </Modal>
    </DivContainer>
  );
}
